import { ReactNode, useEffect } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";

interface HreflangEntry {
  hreflang: string;
  href: string;
}

interface LayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object | object[];
  /** hreflang alternate links for multilingual pages */
  hreflangLinks?: HreflangEntry[];
}

function setMeta(attrName: string, attrValue: string, content: string) {
  let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string, extraAttrs?: Record<string, string>) {
  // For hreflang links, match on both rel and hreflang to allow multiple entries
  const selector = extraAttrs?.hreflang
    ? `link[rel="${rel}"][hreflang="${extraAttrs.hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (extraAttrs) {
      Object.entries(extraAttrs).forEach(([k, v]) => el!.setAttribute(k, v));
    }
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Layout({ children, title, description, canonical, ogImage, schema, hreflangLinks }: LayoutProps) {
  useEffect(() => {
    if (title) {
      document.title = title;
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }

    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }

    const canonicalUrl = canonical || (window.location.origin + window.location.pathname);
    setLink("canonical", canonicalUrl);
    setMeta("property", "og:url", canonicalUrl);

    if (ogImage) {
      setMeta("property", "og:image", ogImage);
      setMeta("name", "twitter:image", ogImage);
    }

    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Local Ranking Coach");
    setMeta("name", "twitter:card", "summary_large_image");

    // Inject hreflang alternate links for multilingual pages
    if (hreflangLinks && hreflangLinks.length > 0) {
      hreflangLinks.forEach(({ hreflang, href }) => {
        setLink("alternate", href, { hreflang });
      });
    }
  }, [title, description, canonical, ogImage, hreflangLinks]);

  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <div className="min-h-screen flex flex-col">
      {/* JSON-LD Schema Markup */}
      {schemas.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }}
        />
      ))}
      <Navbar />
      <main id="main-content" className="flex-1" tabIndex={-1}>
        {children}
      </main>
      <Footer />
    </div>
  );
}
